// RealChem formula DSL parser.
//
// Grammar (informal):
//   formula     := side "->" side ("@" conditions)?
//   side        := term ("+" term)*
//   term        := number? id
//   conditions  := condition ("," condition)*
//   condition   := key "=" value
//
// Example inputs:
//   "2 hydrogen + oxygen -> 2 water @ T=800K, ticks=50"
//   "iron + oxygen -> rust"
//   "uranium_238 -> thorium @ ticks=1000"

import type { Formula, FormulaConditions, FormulaTerm } from "./types";

export class FormulaParseError extends Error {
  constructor(
    message: string,
    public readonly source: string,
    public readonly position: number,
  ) {
    super(`${message} (at offset ${position} of "${source}")`);
    this.name = "FormulaParseError";
  }
}

export function parseFormula(src: string): Formula {
  const trimmed = src.trim();
  const [body, conditionsText] = splitConditions(trimmed);
  const arrowIdx = findArrow(body);
  if (arrowIdx < 0) {
    throw new FormulaParseError("missing '->'", src, body.length);
  }
  const lhs = body.slice(0, arrowIdx).trim();
  const rhs = body.slice(arrowIdx + 2).trim();
  return {
    reactants: parseSide(lhs, src, 0),
    products: parseSide(rhs, src, arrowIdx + 2),
    conditions: parseConditions(conditionsText ?? ""),
  };
}

function splitConditions(s: string): [string, string | null] {
  const at = s.indexOf("@");
  if (at < 0) return [s, null];
  return [s.slice(0, at).trim(), s.slice(at + 1).trim()];
}

function findArrow(s: string): number {
  const i = s.indexOf("->");
  if (i >= 0) return i;
  // Common alternative writings.
  const alt = s.indexOf("→");
  return alt;
}

function parseSide(text: string, source: string, offset: number): FormulaTerm[] {
  if (text.length === 0) {
    throw new FormulaParseError("empty side", source, offset);
  }
  return text.split("+").map((piece, idx) => {
    const trimmed = piece.trim();
    if (trimmed.length === 0) {
      throw new FormulaParseError("empty term", source, offset);
    }
    return parseTerm(trimmed, source, offset + idx);
  });
}

function parseTerm(text: string, source: string, offset: number): FormulaTerm {
  // "2 water" or "2water" or just "water". Coefficient defaults to 1.
  const m = /^(\d+(?:\.\d+)?)?\s*([A-Za-z][A-Za-z0-9_]*)$/.exec(text);
  if (!m) {
    throw new FormulaParseError(`unparsable term '${text}'`, source, offset);
  }
  const n = m[1] ? Number(m[1]) : 1;
  const id = m[2];
  if (!Number.isFinite(n) || n <= 0) {
    throw new FormulaParseError(`non-positive coefficient '${m[1]}'`, source, offset);
  }
  return { n, id };
}

function parseConditions(text: string): FormulaConditions {
  const out: FormulaConditions = {};
  if (text.length === 0) return out;
  for (const piece of text.split(",")) {
    const eq = piece.indexOf("=");
    if (eq < 0) continue;
    const key = piece.slice(0, eq).trim().toLowerCase();
    const value = piece.slice(eq + 1).trim();
    if (key === "t" || key === "temperature") {
      out.temperatureK = parseTemperature(value);
    } else if (key === "ticks") {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) out.ticks = Math.floor(n);
    }
  }
  return out;
}

function parseTemperature(value: string): number | undefined {
  // Accepts "800", "800K", "800k", "800 K".
  const m = /^(-?\d+(?:\.\d+)?)\s*[Kk]?$/.exec(value);
  if (!m) return undefined;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : undefined;
}
